import { useEffect, useState, useRef } from 'react';
import {
  configureBootWarmupRequirements,
  getBootWarmupSnapshot,
  isBootWarmupSteadyState,
  noteBootAuthReady,
  noteBootAuthedApiReady,
  noteBootBatonReady,
  noteBootDaemonIpcReady,
  subscribeBootWarmupSignals,
} from '../lib/bootWarmupSignals';
import { fetchCloudAuthenticated } from '../services/api';
import { getClientTransport } from '../handlers/ClientTransportHandler';

const STRIP_SHOW_DELAY_MS = 800;

function isTauriRuntime() {
  return typeof window !== 'undefined' && '__TAURI__' in window;
}

/**
 * Subscribe to real boot signals and expose strip visibility (after brief delay).
 */
export function useBootWarmupStrip(auth) {
  const [, tick] = useState(0);
  const [stripVisible, setStripVisible] = useState(false);
  const showTimerRef = useRef(null);
  const authedProbeStartedRef = useRef(false);

  useEffect(() => {
    configureBootWarmupRequirements({
      requireDaemonIpc: isTauriRuntime(),
      requireBaton: false,
    });
    return subscribeBootWarmupSignals(() => tick((n) => n + 1));
  }, [auth?.isAuthenticated]);

  useEffect(() => {
    const ready = !auth?.isLoading
      && !!auth?.isAuthenticated
      && !!auth?.accessToken
      && !auth?.sessionRevoked;
    noteBootAuthReady(ready);
  }, [
    auth?.isLoading,
    auth?.isAuthenticated,
    auth?.accessToken,
    auth?.sessionRevoked,
  ]);

  useEffect(() => {
    if (!auth?.isAuthenticated || !auth?.accessToken || auth?.isLoading) {
      authedProbeStartedRef.current = false;
      noteBootAuthedApiReady(false);
      return undefined;
    }
    if (authedProbeStartedRef.current) return undefined;
    authedProbeStartedRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetchCloudAuthenticated('/api/auth/user', { method: 'GET' });
        if (!cancelled && resp?.ok) noteBootAuthedApiReady(true);
      } catch {
        if (!cancelled) noteBootAuthedApiReady(false);
      }
    })();
    return () => { cancelled = true; };
  }, [auth?.isAuthenticated, auth?.accessToken, auth?.isLoading]);

  useEffect(() => {
    if (!isTauriRuntime()) {
      noteBootDaemonIpcReady(true);
      return undefined;
    }
    let cancelled = false;
    const syncDaemon = async () => {
      try {
        const gate = await import('../services/localLibraryIpcGate');
        if (cancelled) return;
        if (gate.isLocalCatalogBrowseReady?.()) {
          noteBootDaemonIpcReady(true);
          return;
        }
        const { isConnectorAvailable } = await import('../lib/connector');
        if (cancelled) return;
        noteBootDaemonIpcReady(!!isConnectorAvailable());
      } catch {
        if (!cancelled) noteBootDaemonIpcReady(false);
      }
    };
    void syncDaemon();
    const onCatalogReady = () => noteBootDaemonIpcReady(true);
    window.addEventListener('maestro:local-catalog-ready', onCatalogReady);
    return () => {
      cancelled = true;
      window.removeEventListener('maestro:local-catalog-ready', onCatalogReady);
    };
  }, []);

  useEffect(() => {
    if (!auth?.isAuthenticated) {
      noteBootBatonReady(true);
      return undefined;
    }
    const transport = getClientTransport();
    const sync = (state) => {
      noteBootBatonReady(state === 'connected');
    };
    sync(transport.getConnectionState());
    return transport.onConnectionChanged(sync);
  }, [auth?.isAuthenticated, auth?.accessToken]);

  useEffect(() => {
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    if (isBootWarmupSteadyState()) {
      setStripVisible(false);
      return undefined;
    }
    showTimerRef.current = setTimeout(() => {
      showTimerRef.current = null;
      if (!isBootWarmupSteadyState()) setStripVisible(true);
    }, STRIP_SHOW_DELAY_MS);
    return () => {
      if (showTimerRef.current) {
        clearTimeout(showTimerRef.current);
        showTimerRef.current = null;
      }
    };
  }, [tick]);

  const snapshot = getBootWarmupSnapshot();
  return {
    stripVisible: stripVisible && snapshot.visible,
    message: snapshot.message,
    phase: snapshot.phase,
    snapshot,
  };
}
